import { OptionControlData } from './controls/OptionControl';
import { StatementControlData } from './controls/StatementControl';
import { TextAreaControlData } from './controls/TextAreaControl';
import { DataNode, NodeData } from './dataNode';

export type TranslationResult = {
  questionText: string;
  choices?: (string | null | undefined)[];
  statements?: (string | null | undefined)[];
};

export function getDefaultSourceTranslation(nodeData: NodeData) {
  return nodeData.originalQuestionDefinition?.translations.find((t) => t.isDefault);
}

export function getSourceTranslationContent(sourceTrans: ReturnType<typeof getDefaultSourceTranslation>) {
  return {
    questionText: sourceTrans?.text ?? '',
    choices: (sourceTrans?.choices ?? []).filter((c): c is string => !!c),
    statements: (sourceTrans?.statements ?? []).filter((s): s is string => !!s),
  };
}

function restoreTranslatedArrayToSourcePositions<T>(
  source: (T | null | undefined)[],
  translated: (T | null | undefined)[],
): (T | null | undefined)[] {
  const nonNullCount = source.filter((item) => !!item).length;
  if (translated.length === source.length) {
    return translated;
  }
  if (translated.length !== nonNullCount) {
    return translated;
  }
  const result: (T | null | undefined)[] = source.map(() => null);
  let compactIndex = 0;
  source.forEach((item, sourceIndex) => {
    if (item) {
      result[sourceIndex] = translated[compactIndex] ?? null;
      compactIndex++;
    }
  });
  return result;
}

function normalizeTranslationResult(
  sourceTrans: ReturnType<typeof getDefaultSourceTranslation>,
  result: TranslationResult,
): TranslationResult {
  if (!sourceTrans) return result;
  return {
    questionText: result.questionText,
    choices: restoreTranslatedArrayToSourcePositions(sourceTrans.choices ?? [], result.choices ?? []),
    statements: restoreTranslatedArrayToSourcePositions(sourceTrans.statements ?? [], result.statements ?? []),
  };
}

export function applyTranslationResultToNode(
  node: DataNode,
  result: TranslationResult,
  sourceTrans?: ReturnType<typeof getDefaultSourceTranslation>,
): void {
  const normalized = sourceTrans ? normalizeTranslationResult(sourceTrans, result) : result;
  const questionTextControl = node.controls.questionText as TextAreaControlData | undefined;
  if (questionTextControl && result.questionText) {
    questionTextControl.initialValue = result.questionText;
    questionTextControl.initialValueChangedIndicator = (questionTextControl.initialValueChangedIndicator ?? 0) + 1;
    questionTextControl.change(result.questionText);
  }

  normalized.choices?.forEach((choice, index) => {
    if (!choice) return;
    const optionControl = node.controls[`opt-${index}`] as OptionControlData | undefined;
    if (!optionControl) return;
    optionControl.initialValue = choice;
    optionControl.initialValueChangedIndicator = (optionControl.initialValueChangedIndicator ?? 0) + 1;
    optionControl.onChange?.(choice);
  });

  normalized.statements?.forEach((statement, index) => {
    if (!statement) return;
    const statementControl = node.controls[`statement-${index}`] as StatementControlData | undefined;
    if (!statementControl) return;
    statementControl.initialValue = statement;
    statementControl.initialValueChangedIndicator = (statementControl.initialValueChangedIndicator ?? 0) + 1;
    statementControl.onChange?.(statement);
  });

  node.data.translationStatusOverride = 'ok';
}
